<template>
  <a-modal
    class="cfg-modal"
    width="560px"
    v-model="visable"
    centered
    destroyOnClose
    :confirmLoading="okLoading"
    :maskClosable="false"
  >
    <!-- 自定义标题 -->
    <div slot="title" class="cfg-title">
      <span class="cfg-title__icon"><a-icon type="control" theme="filled" /></span>
      <span>{{ initData.姓名 + $t('supervise.personalConfig') }}</span>
    </div>

    <!-- 自定义底部按钮 -->
    <div slot="footer" class="cfg-footer">
      <a-button @click="handleCancel">取消</a-button>
      <a-button type="primary" :loading="okLoading" @click="handleOk">确定</a-button>
    </div>

    <div class="cfg">
      <!-- 一、设备与功耗 -->
      <section class="cfg-group">
        <header class="cfg-group__hd">
          <a-icon type="thunderbolt" /><span>设备与功耗</span>
        </header>
        <div class="cfg-row">
          <div class="cfg-row__main">
            <div class="cfg-row__title">{{ $t('global.zdjpkg') }}</div>
            <div class="cfg-row__desc">设备静止时自动降低上报频率，延长续航</div>
          </div>
          <div class="cfg-row__ctrl">
            <span class="cfg-state" :class="{ 'is-on': enableZdjpkg }">{{ enableZdjpkg ? '已开启' : '已关闭' }}</span>
            <a-switch v-model="enableZdjpkg" @change="onZdjpkgChange" checked-children="开启" un-checked-children="关闭" />
          </div>
        </div>
      </section>

      <!-- 二、定位采集 -->
      <section class="cfg-group">
        <header class="cfg-group__hd">
          <a-icon type="environment" /><span>定位采集</span>
        </header>
        <div class="cfg-freq">
          <div class="cfg-freq__label">
            <a-icon type="clock-circle" />{{ $t('global.dwpl') }}
          </div>
          <div v-for="(item, index) in locateCustomLocate" :key="index" class="cfg-freq__row">
            <span class="time-range">{{ formatTime(item.startTime) }} ~ {{ formatTime(item.endTime) }}</span>
            <span class="cfg-freq__sep">采集间隔</span>
            <a-select size="small" class="cfg-select" v-model="item.intervalTime">
              <a-select-option v-for="opt in freqOptions" :key="opt.key" :value="opt.key">
                {{ opt.label }}
              </a-select-option>
            </a-select>
          </div>
          <div class="cfg-row">
            <div class="cfg-row__main">
              <div class="cfg-row__title">{{ $t('global.gpsyx') }}</div>
              <div class="cfg-row__desc">开启GPS定位采集，提高定位精度</div>
            </div>
            <div class="cfg-row__ctrl">
              <span class="cfg-state" :class="{ 'is-on': enableLocateGps }">{{ enableLocateGps ? '已开启' : '已关闭' }}</span>
              <a-switch v-model="enableLocateGps" @change="onGpsChange" checked-children="开启" un-checked-children="关闭" />
            </div>
          </div>
        </div>
      </section>

      <!-- 三、健康监测 -->
      <section class="cfg-group">
        <header class="cfg-group__hd">
          <a-icon type="heart" /><span>健康监测</span>
        </header>
        <div class="cfg-row">
          <div class="cfg-row__main">
            <div class="cfg-row__title">{{ $t('global.pdjckg') }}</div>
            <div class="cfg-row__desc">实时检测佩戴状态，脱腕时自动上报</div>
          </div>
          <div class="cfg-row__ctrl">
            <span class="cfg-state" :class="{ 'is-on': enablePdjckg }">{{ enablePdjckg ? '已开启' : '已关闭' }}</span>
            <a-switch v-model="enablePdjckg" @change="onPdjckgChange" checked-children="开启" un-checked-children="关闭" />
          </div>
        </div>
        <div class="cfg-row">
          <div class="cfg-row__main">
            <div class="cfg-row__title">{{ $t('global.smtzkg') }}</div>
            <div class="cfg-row__desc">开启心率、血压等生命体征数据采集</div>
          </div>
          <div class="cfg-row__ctrl">
            <span class="cfg-state" :class="{ 'is-on': enableSmtzkg }">{{ enableSmtzkg ? '已开启' : '已关闭' }}</span>
            <a-switch v-model="enableSmtzkg" @change="onSmtzkgChange" checked-children="开启" un-checked-children="关闭" />
          </div>
        </div>
        <div class="cfg-freq">
          <div class="cfg-freq__label">
            <a-icon type="clock-circle" />{{ $t('global.healthFreq') }}
          </div>
          <div v-for="(item, index) in healthCustomLocate" :key="index" class="cfg-freq__row">
            <span class="time-range">{{ formatTime(item.startTime) }} ~ {{ formatTime(item.endTime) }}</span>
            <span class="cfg-freq__sep">采集间隔</span>
            <a-select size="small" class="cfg-select" v-model="item.intervalTime">
              <a-select-option v-for="opt in freqOptions" :key="opt.key" :value="opt.key">
                {{ opt.label }}
              </a-select-option>
            </a-select>
          </div>
        </div>
      </section>

      <!-- 四、安全告警 -->
      <section class="cfg-group">
        <header class="cfg-group__hd">
          <a-icon type="safety-certificate" /><span>安全告警</span>
        </header>
        <div class="cfg-row">
          <div class="cfg-row__main">
            <div class="cfg-row__title">{{ $t('global.enableSos') }}</div>
            <div class="cfg-row__desc">长按按键触发一键求救并上报平台</div>
          </div>
          <div class="cfg-row__ctrl">
            <span class="cfg-state" :class="{ 'is-on': enableSos }">{{ enableSos ? '已开启' : '已关闭' }}</span>
            <a-switch v-model="enableSos" @change="onSosChange" checked-children="开启" un-checked-children="关闭" />
          </div>
        </div>
        <div class="cfg-row">
          <div class="cfg-row__main">
            <div class="cfg-row__title">{{ $t('global.enableShake') }}</div>
            <div class="cfg-row__desc">设备震动反馈</div>
          </div>
          <div class="cfg-row__ctrl">
            <span class="cfg-state" :class="{ 'is-on': enableShake }">{{ enableShake ? '已开启' : '已关闭' }}</span>
            <a-switch v-model="enableShake" @change="onShakeChange" checked-children="开启" un-checked-children="关闭" />
          </div>
        </div>
      </section>
    </div>
  </a-modal>
</template>
<script>
  import config from '../../config/'

const FULL_DAY_START = 0                    // 00:00:00
const FULL_DAY_END = 24 * 60 * 60 - 1       // 23:59:59 = 86399，后端按秒存储
const DEFAULT_INTERVAL = 1800

// 固定全天一组
const createFullDayItem = (intervalTime) => ({
  startTime: FULL_DAY_START,
  endTime: FULL_DAY_END,
  intervalTime: intervalTime || DEFAULT_INTERVAL
})
  export default {
    data() {
      return {
        locateCustomLocate: [createFullDayItem()],
        healthCustomLocate: [createFullDayItem()],
        enableLocateGps:false,
        // 确定按钮加载状态
        okLoading: false,
        enableSos:false,
        enableZdjpkg:true,
        enablePdjckg:false,
        enableSmtzkg:false,
        enableShake:false,
        // 用户配置属性
        locateFreq: 600,    
        locateFreqTemp: null,    
        locateFreqChange:false,
        enableLocateGpsTemp:null,
        enableLocateGpsChange:false,
        personId: '',
        visable: false,
        initData: '',
        groupConfig: '',
        personalConfig: '',  
        addTimeFlag:true, 
      }
    },
    computed: {
    freqOptions() {
      let freq = []
      for (let i = 1; i < 60; i++) {
        freq.push({ key: i * 60, label: i + this.$t('global.minute') })
      }
      for (let i = 1; i <= 24; i++) {
        freq.push({ key: i * 60 * 60, label: i + this.$t('global.hour') })
      }
      return freq
    }
      
    },
    methods: {
      formatTime(sec) {
        const s = Math.max(0, Number(sec) || 0)
        const h = Math.floor(s / 3600)
        const m = Math.floor((s % 3600) / 60)
        const ss = s % 60
        const p = n => String(n).padStart(2, '0')
        return `${p(h)}:${p(m)}:${p(ss)}`
      },
    // 只保留一组全天，沿用后端返回的间隔
    toFullDayList(list, defaultInterval) {
      const interval = (list && list.length && list[0].intervalTime) || defaultInterval
      return [createFullDayItem(interval)]
    },

      onGpsChange(checked) {
        console.log(`enableGps ${checked}`);
        this.enableLocateGps = checked
        // this.enableLocateGpsChange = checked != this.enableLocateGpsTemp
        // console.log(`enableLocateGpsChange ${this.enableLocateGpsChange}`);
      }, 
      onSosChange(checked) {
        console.log(`enableSos ${checked}`);
        this.enableSos = checked
      },
      onZdjpkgChange(checked) {
        console.log(`enableZdjpkg ${checked}`);
        this.enableZdjpkg = checked
      },
      onPdjckgChange(checked) {
        console.log(`enablePdjckg ${checked}`);
        this.enablePdjckg = checked
      },
      onSmtzkgChange(checked) {
        console.log(`enableSmtzkg ${checked}`);
        this.enableSmtzkg = checked
      },
      onShakeChange(checked) {
        console.log(`enableShake ${checked}`);
        this.enableShake = checked
      },
      handleChange(value) {
        console.log(`selected ${value}`); 
      },
      // 修改个人配置参数
    modifiedPersonalConfig() {
      this.okLoading = true
      let newConfig = {
        enableSos: this.enableSos,
        enableAutoFreq: this.enableZdjpkg,
        enableWear: this.enablePdjckg,
        enableHealth: this.enableSmtzkg,
        enableShake: this.enableShake,
        enableLocateGps: this.enableLocateGps,
        id: this.personId,
        oui: 'ZX',
        deviceType: 'Z32S',
        locateCustomLocate: this.locateCustomLocate.map(item => ({
          startTime: Number(item.startTime) || 0,
          endTime: Number(item.endTime),
          intervalTime: item.intervalTime
        })),
        healthCustomLocate: this.healthCustomLocate.map(item => ({
          startTime: Number(item.startTime) || 0,
          endTime: Number(item.endTime),
          intervalTime: item.intervalTime
        }))
      }
        console.log(newConfig);
        
        // if(!this.locateFreqChange && !this.enableLocateGpsChange){
        //   this.okLoading = false
        //   this.$notification.error({
        //       message: '配置未发生改变'
        //     })
        // }else{
          this.$http.put(config.api.put_personnal_config, newConfig).then((res) => {
            this.okLoading = false
            if (res.code === 0) {
              this.$notification.success({
                message: this.$t('global.commitSuccess')
              })
              this.visable = false
            } else {
              this.$notification.error({
                message: `${res.desc}`
              })
            }
          })
        // }
       
      },
      handleOk() {
        // console.log('ok')
        this.modifiedPersonalConfig()
      },
      handleCancel() {
        this.visable = false
        this.okLoading = false
      },
      showModal(options) {
        console.log(options)
        this.visable = true
        this.initData = options
        this.personId = options.id 
        this.getPersonalConfig(this.personId) 
      },
      // 获取个人监管配置
     getPersonalConfig(personId) {
      return new Promise((resolve) => {
        this.$http.get(config.api.get_personnal_config + personId).then((res) => {
          let { code, desc, result } = res
          if (code === 0 && desc === 'success' && result) {
            // this.locateFreq = result.personalConfigModel.locateFreq 
            this.enableSos = result.personalConfigModel.enableSos || false
            this.enableZdjpkg = result.personalConfigModel.enableAutoFreq || false
            this.enablePdjckg = result.personalConfigModel.enableWear || false
            this.enableSmtzkg = result.personalConfigModel.enableHealth || false
            this.enableShake = result.personalConfigModel.enableShake || false
            this.enableLocateGps = result.personalConfigModel.enableLocateGps || false
            this.locateCustomLocate = this.toFullDayList(
              result.personalConfigModel.locateCustomLocate, this.locateFreq || DEFAULT_INTERVAL)
            this.healthCustomLocate = this.toFullDayList(
              result.personalConfigModel.healthCustomLocate, DEFAULT_INTERVAL)
          }
        })
      })
    },
      addTime(type) {
        if(type == 'locate'){
          this.locateCustomLocate.push({
            startTime: this.$moment().startOf('day'),
            endTime: this.$moment(),
            intervalTime: 900
          })
        }else{
          this.healthCustomLocate.push({
            startTime: this.$moment().startOf('day'),
            endTime: this.$moment(),
            intervalTime: 900
          })
        }
      },
      deleteTime(index, type) {
        if(type == 'locate'){
          this.locateCustomLocate.splice(index, 1)
        }else{
          this.healthCustomLocate.splice(index, 1)
        }
      },
    }
  }
</script>
<style lang="less" scoped>
@import '../../theme/color.less';

@cfg-primary: #2f6bff;   // 可替换为 @primary-color
@cfg-line: #edf0f5;
@cfg-sub: rgba(0, 0, 0, 0.45);

/* ---------- 弹窗外壳 ---------- */
.cfg-modal {
  /deep/ .ant-modal-content {
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(15, 34, 67, 0.15);
  }
  /deep/ .ant-modal-header {
    padding: 16px 20px;
    border-bottom: 1px solid #f2f4f7;
  }
  /deep/ .ant-modal-body {
    padding: 14px 20px 4px;
  }
  /deep/ .ant-modal-footer {
    padding: 12px 20px;
    border-top: 1px solid #f2f4f7;
  }
}

.cfg-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    margin-right: 10px;
    border-radius: 8px;
    color: #fff;
    font-size: 14px;
    background: linear-gradient(135deg, @cfg-primary, #6b9bff);
    box-shadow: 0 3px 8px rgba(47, 107, 255, 0.28);
  }
}

.cfg-footer {
  display: flex;
  justify-content: flex-end;
  .ant-btn + .ant-btn {
    margin-left: 10px;
  }
}

/* ---------- 主体滚动区 ---------- */
.cfg {
  max-height: 58vh;
  overflow-y: auto;
  padding-right: 4px;
}

/* ---------- 分组卡片 ---------- */
.cfg-group {
  background: #fafbfd;
  border: 1px solid @cfg-line;
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 12px;

  &__hd {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    font-size: 13px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.55);
    letter-spacing: 0.5px;

    .anticon {
      color: @cfg-primary;
      font-size: 14px;
    }
  }
}

/* ---------- 开关行 ---------- */
.cfg-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 10px;
  border-radius: 8px;
  transition: background 0.2s ease;

  & + .cfg-row {
    border-top: 1px dashed #e8ecf3;
  }

  &:hover {
    background: #fff;
    box-shadow: 0 2px 10px rgba(15, 34, 67, 0.06);
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
  }

  &__desc {
    margin-top: 2px;
    font-size: 12px;
    color: @cfg-sub;
  }

  &__ctrl {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
}

.cfg-state {
  margin-right: 10px;
  padding: 1px 8px;
  font-size: 12px;
  line-height: 18px;
  border-radius: 10px;
  color: @cfg-sub;
  background: #eef0f4;
  transition: all 0.2s ease;

  &.is-on {
    color: @cfg-primary;
    background: rgba(47, 107, 255, 0.1);
  }
}

/* ---------- 采集频率块 ---------- */
.cfg-freq {
  margin: 8px 0 6px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #eceff4;
  border-radius: 10px;

  &__label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.75);

    .anticon {
      color: @cfg-sub;
    }
  }

  &__row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin: 6px 0;
  }

  &__sep {
    margin: 0 10px;
    font-size: 12px;
    color: @cfg-sub;
  }

  .time-range {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 6px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.75);
    background: #f3f6ff;
    font-family: 'DIN Alternate', Menlo, Consolas, monospace;
  }
}

.cfg-select {
  width: 120px;
  /deep/ .ant-select-selection {
    border-radius: 6px;
  }
}
</style>